import React, { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, Copy, Send, RefreshCw, CheckCircle2, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { api } from "../../lib/api";

const CONFIDENCE_TONE = {
  high: "text-safe",
  medium: "text-gold",
  low: "text-sev-medium",
};

function PatchBlock({ patch }) {
  if (!patch) return null;
  const kind = patch.kind || "manual";
  return (
    <div className="rounded-lg border border-line bg-panel" data-testid="ai-patch-block">
      <div className="flex items-center justify-between px-3 py-2 border-b border-line text-xs uppercase tracking-wider">
        <span className="text-ink/60">{kind === "diff" ? "Unified diff" : kind === "dependency-bump" ? "Dependency bump" : kind === "config" ? "Config change" : "Manual fix"}</span>
        {patch.language && <span className="font-mono text-ink/40">{patch.language}</span>}
      </div>
      {kind === "diff" && patch.diff ? (
        <pre className="p-3 text-xs font-mono whitespace-pre overflow-x-auto text-ink" data-testid="ai-patch-diff">{patch.diff}</pre>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-line">
          {patch.before ? (
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-sev-critical mb-1.5">Before</div>
              <pre className="text-xs font-mono whitespace-pre-wrap text-ink/90">{patch.before}</pre>
            </div>
          ) : null}
          {patch.after ? (
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-safe mb-1.5">After</div>
              <pre className="text-xs font-mono whitespace-pre-wrap text-ink/90" data-testid="ai-patch-after">{patch.after}</pre>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function AIFixPanel({ finding, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState({ patch: null, messages: [], session_id: null });
  const [chatText, setChatText] = useState("");
  const [applying, setApplying] = useState(false);
  const scrollRef = useRef(null);

  // load cached session on open
  useEffect(() => {
    if (!finding?.id) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/findings/${finding.id}/ai-fix`)
      .then((r) => {
        if (cancelled) return;
        setData({
          patch: r.data?.patch || null,
          messages: r.data?.messages || [],
          session_id: r.data?.session_id || null,
        });
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [finding?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data.messages, data.patch]);

  const generate = async () => {
    if (!finding?.id) return;
    setGenerating(true);
    try {
      const r = await api.post(`/findings/${finding.id}/ai-fix`);
      setData({
        patch: r.data?.patch || null,
        messages: r.data?.messages || [],
        session_id: r.data?.session_id || null,
      });
      toast.success("ArgusSecure Fix-It ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI agent unavailable. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  const send = async () => {
    const text = chatText.trim();
    if (!text || sending) return;
    setSending(true);
    setChatText("");
    // optimistic
    setData((d) => ({
      ...d,
      messages: [...(d.messages || []), { role: "user", content: text, at: new Date().toISOString() }],
    }));
    try {
      const r = await api.post(`/findings/${finding.id}/ai-chat`, { message: text });
      setData({
        patch: r.data?.patch ?? data.patch,
        messages: r.data?.messages || [],
        session_id: r.data?.session_id || data.session_id,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI agent error.");
    } finally {
      setSending(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text || "");
    toast.success("Copied to clipboard.");
  };

  const applyFix = async () => {
    if (!finding?.id) return;
    setApplying(true);
    try {
      await api.patch(`/findings/${finding.id}`, {
        status: "fixed",
        comment: `ArgusSecure Fix-It applied. ${(data.patch?.explanation || "").slice(0, 200)}`,
      });
      toast.success("Marked as fixed.");
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mark fixed.");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-ink/60" data-testid="ai-loading">
        <Loader2 className="animate-spin mr-2" size={16} /> Checking for a prior session…
      </div>
    );
  }

  const patch = data.patch;
  const hasPatch = patch && (patch.patch || patch.explanation || patch.steps);

  // First-time empty state
  if (!hasPatch && (data.messages || []).length === 0) {
    return (
      <div className="py-6 space-y-4" data-testid="ai-empty">
        <div className="flex items-start gap-3">
          <Sparkles size={20} className="text-gold mt-0.5" />
          <div>
            <h3 className="font-display text-xl">ArgusSecure Fix-It Agent</h3>
            <p className="text-sm text-ink/70 mt-1">
              A grounded AI engineer reads this finding's evidence and proposes a concrete patch — diff,
              dependency bump, or config change. You stay in control of merge.
            </p>
          </div>
        </div>
        <div className="text-xs text-ink/50 flex items-center gap-2">
          <ShieldCheck size={12} className="text-safe" /> Powered by Claude Sonnet 4.5 · No code is stored externally.
        </div>
        <Button
          onClick={generate}
          disabled={generating}
          className="bg-gold text-paper hover:bg-gold2 h-12 px-6 font-semibold"
          data-testid="ai-generate"
        >
          {generating ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" /> Generating fix…
            </>
          ) : (
            <>
              <Sparkles size={16} className="mr-2" /> Generate fix with AI
            </>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ai-panel">
      {patch?.explanation ? (
        <div className="rounded-lg bg-panel border border-line p-3 text-sm text-ink/85 leading-relaxed" data-testid="ai-explanation">
          {patch.explanation}
        </div>
      ) : null}

      {patch?.confidence ? (
        <div className="flex items-center gap-3 text-xs">
          <span className="uppercase tracking-wider text-ink/50">Confidence</span>
          <span className={`font-semibold uppercase ${CONFIDENCE_TONE[patch.confidence] || "text-ink"}`} data-testid="ai-confidence">
            {patch.confidence}
          </span>
        </div>
      ) : null}

      {patch?.patch ? <PatchBlock patch={patch.patch} /> : null}

      {(patch?.steps || []).length > 0 ? (
        <div>
          <div className="text-xs uppercase tracking-wider text-ink/50 mb-2">Apply steps</div>
          <ol className="list-decimal pl-5 space-y-1 text-sm text-ink/80" data-testid="ai-steps">
            {patch.steps.map((s, i) => (
              <li key={`step-${i}-${s.slice(0, 24)}`}>{s}</li>
            ))}
          </ol>
        </div>
      ) : null}

      {patch?.verification ? (
        <div>
          <div className="text-xs uppercase tracking-wider text-ink/50 mb-1">Verify it worked</div>
          <p className="text-sm text-ink/80" data-testid="ai-verification">{patch.verification}</p>
        </div>
      ) : null}

      {(patch?.references || []).length > 0 ? (
        <div className="pt-2 border-t border-line">
          <div className="text-xs uppercase tracking-wider text-ink/50 mb-2">References</div>
          <ul className="space-y-1">
            {patch.references.map((r, i) => (
              <li key={r.url || `ref-${i}`}>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-gold hover:underline inline-flex items-center gap-1"
                >
                  {r.label} <ExternalLink size={12} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-line">
        <Button
          variant="outline"
          className="border-line bg-transparent text-ink hover:bg-raised"
          onClick={() =>
            copy(
              patch?.patch?.diff ||
                patch?.patch?.after ||
                JSON.stringify(patch?.patch || {}, null, 2),
            )
          }
          data-testid="ai-copy"
        >
          <Copy size={14} className="mr-1.5" /> Copy patch
        </Button>
        <Button
          variant="outline"
          className="border-line bg-transparent text-ink hover:bg-raised"
          onClick={generate}
          disabled={generating}
          data-testid="ai-regenerate"
        >
          <RefreshCw size={14} className={`mr-1.5 ${generating ? "animate-spin" : ""}`} /> Regenerate
        </Button>
        <Button
          className="bg-gold text-paper hover:bg-gold2 font-semibold"
          onClick={applyFix}
          disabled={applying}
          data-testid="ai-apply"
        >
          <CheckCircle2 size={14} className="mr-1.5" /> {applying ? "Marking…" : "Mark as fixed"}
        </Button>
      </div>

      {/* Chat thread */}
      {(data.messages || []).length > 1 ? (
        <div className="pt-4 border-t border-line">
          <div className="text-xs uppercase tracking-wider text-ink/50 mb-2">Refine the fix</div>
          <div
            ref={scrollRef}
            className="max-h-64 overflow-y-auto space-y-2 pr-1"
            data-testid="ai-thread"
          >
            {data.messages.slice(1).map((m, i) => (
              <div
                key={`msg-${m.at || i}-${m.role}`}
                className={`rounded-md p-2.5 text-sm ${m.role === "user" ? "bg-raised text-ink" : "bg-panel border border-line text-ink/85"}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-ink/40 mb-1">
                  {m.role === "user" ? "You" : "Fix-It"}
                </div>
                <div className="whitespace-pre-wrap break-words font-mono text-xs">{m.content}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="pt-2 flex items-end gap-2">
        <Textarea
          value={chatText}
          onChange={(e) => setChatText(e.target.value)}
          placeholder="Ask a follow-up — e.g. 'use parameterised query instead' or 'show me the fix for express 5'."
          className="min-h-[60px] resize-none bg-paper border-line text-ink"
          data-testid="ai-chat-input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <Button
          onClick={send}
          disabled={sending || !chatText.trim()}
          className="bg-gold text-paper hover:bg-gold2 h-10 self-end"
          data-testid="ai-send"
        >
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </Button>
      </div>
    </div>
  );
}
